import { useMutation } from "@apollo/client/react";
import { useModal } from "@ebay/nice-modal-react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import FormFieldSpace from "components/FormFields/Space";
import FormFieldTags from "components/FormFields/Tags";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { PluginInstallInput, PluginTemplateParameter } from "types/generated";
import { getDrawerFormFix } from "utils/css";
import { getDocsUrl } from "utils/getDocsUrl";

import { INSTALL_PLUGIN_TEMPLATE } from "./gql";
import ParameterInput from "./ParameterInputField";
import { InstallPluginFormValues } from "./types";

type InstallPluginDrawerProps = {
  title: string;
  parameters: PluginTemplateParameter[];
  name: string;
  labels: string[];
  templateId: string;
};

const InstallPluginDrawer = createDrawer(
  ({ title, parameters, name, labels, templateId }: InstallPluginDrawerProps) => {
    const drawer = useModal();
    const navigate = useNavigate();

    const [installPluginTemplate, { loading }] = useMutation(INSTALL_PLUGIN_TEMPLATE, {
      onCompleted: ({ pluginInstall }) => {
        if (pluginInstall?.id) {
          drawer.resolve();
          drawer.hide();
          navigate("/plugins");
        }
      },
    });
    const installPluginForm = useForm<InstallPluginFormValues>({
      defaultValues: {
        labelIdentifier: "",
        name,
        labels: labels.map((value) => ({ value })),
        parameters: parameters.map((parameter) => ({
          id: parameter.id,
          value: parameter.default ?? "",
        })),
      },
    });

    const {
      formState: { errors, isDirty },
      handleSubmit,
      register,
    } = installPluginForm;

    const handleInstallPlugin = (data: InstallPluginFormValues) => {
      const input: PluginInstallInput = {
        labelIdentifier: data.labelIdentifier,
        name: data.name,
        pluginTemplateID: templateId,
        labels: data.labels.map((label) => label.value),
        parameters: data.parameters.map((param) => {
          let parameterDefault: string | null | undefined = null;
          if (!param.value) {
            parameterDefault = parameters.find((p) => p.id === param.id)?.default;
          }

          return {
            id: param.id,
            value: param.value?.toString() ?? parameterDefault ?? "",
          };
        }),
        space: data.space,
      };

      installPluginTemplate({
        variables: {
          input,
        },
      });
    };

    return (
      <DrawerForm isDirty={isDirty}>
        <FormProvider {...installPluginForm}>
          <form onSubmit={handleSubmit(handleInstallPlugin)} {...getDrawerFormFix()}>
            <DrawerHeader justify="between">
              <DrawerHeaderTitle title={title} />
              <DrawerCloseIcon />
            </DrawerHeader>
            <DrawerBody gap="large">
              <FormField label="Installation name" error={errors?.name?.message} noMargin>
                {({ ariaInputProps }) => (
                  <Input
                    placeholder="Enter installation name"
                    error={!!errors?.name}
                    {...register("name", {
                      required: "Installation name is required",
                      setValueAs: (value) => value.trim(),
                    })}
                    {...ariaInputProps}
                  />
                )}
              </FormField>

              <FormField
                label="Stack label"
                error={errors?.labelIdentifier?.message}
                noMargin
                tooltipInfo={
                  <>
                    <TooltipModalBody>
                      The stack label can be used to attach the plugin to a specific resource in
                      spacelift.
                    </TooltipModalBody>
                    <ReadMoreDocsLink
                      docsUrl={getDocsUrl("/integrations/plugins#installing-plugins")}
                    />
                  </>
                }
                tooltipInfoVariant="modal"
              >
                {({ ariaInputProps }) => (
                  <Input
                    placeholder="Enter stack label"
                    error={!!errors?.labelIdentifier}
                    {...register("labelIdentifier", {
                      required: "Stack label is required",
                      setValueAs: (value) => value.trim(),
                    })}
                    {...ariaInputProps}
                  />
                )}
              </FormField>

              <FormFieldSpace noMargin />

              <FormFieldTags name="labels" tagName="label" label="Labels" isOptional noMargin />

              {parameters.map((param, i) => (
                <FormField
                  key={param.id}
                  label={param.name}
                  error={errors?.parameters?.[i]?.value?.message}
                  noMargin
                  isOptional={!param.required}
                  tooltipInfo={param.description}
                  helperText={param.default ? `Defaults to "${param.default}"` : undefined}
                >
                  {({ ariaInputProps }) => (
                    <ParameterInput param={param} index={i} ariaInputProps={ariaInputProps} />
                  )}
                </FormField>
              ))}
            </DrawerBody>
            <DrawerFooter>
              <DrawerFooterActions>
                <DrawerCancelButton />

                <Button variant="primary" type="submit" disabled={loading} loading={loading}>
                  Install
                </Button>
              </DrawerFooterActions>
            </DrawerFooter>
          </form>
        </FormProvider>
      </DrawerForm>
    );
  }
);

export const showInstallPluginDrawer = createDrawerTrigger(InstallPluginDrawer);
